import { z } from 'zod';
import { cursorPageSchema } from '../api/http.js';
import { isoDateTimeSchema, reasonSchema, uuidSchema, versionSchema, } from '../common/primitives.js';
export const TOPIC_STATUSES = [
    'DRAFT',
    'OPEN',
    'DISCUSSING',
    'AWAITING_VOTE',
    'DECIDED',
    'EXECUTION',
    'CLOSED',
    'DEFERRED',
    'CANCELLED',
    'MEETING_REQUIRED',
    'NO_ACTION',
    'QUORUM_NOT_REACHED',
];
export const topicStatusSchema = z.enum(TOPIC_STATUSES);
/** Statuses after which a topic is read-only (history stays visible). */
export const TERMINAL_TOPIC_STATUSES = ['CLOSED', 'CANCELLED', 'NO_ACTION'];
/**
 * Statuses a facilitator may set by hand. `AWAITING_VOTE`, `DECIDED` and `QUORUM_NOT_REACHED` are
 * only reached through voting rounds and decisions, so the chain Topic → Vote → Decision is kept.
 */
export const MANUAL_TOPIC_STATUSES = [
    'OPEN',
    'DISCUSSING',
    'EXECUTION',
    'CLOSED',
    'DEFERRED',
    'CANCELLED',
    'MEETING_REQUIRED',
    'NO_ACTION',
];
export const manualTopicStatusSchema = z.enum(MANUAL_TOPIC_STATUSES);
export const PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'CRITICAL'];
export const prioritySchema = z.enum(PRIORITIES);
export const TOPIC_VISIBILITIES = ['WORKSPACE', 'RESTRICTED'];
export const topicVisibilitySchema = z.enum(TOPIC_VISIBILITIES);
/** REQUIRED/OPTIONAL participants; ACCESS_ONLY only grants read access to a RESTRICTED topic. */
export const PARTICIPANT_KINDS = ['REQUIRED', 'OPTIONAL', 'ACCESS_ONLY'];
export const participantKindSchema = z.enum(PARTICIPANT_KINDS);
export const memberRefSchema = z.object({ memberId: uuidSchema, displayName: z.string() });
export const categorySchema = z.object({ id: uuidSchema, slug: z.string(), name: z.string() });
export const categoryListSchema = z.object({ items: z.array(categorySchema) });
export const topicTitleSchema = z.string().trim().min(3).max(200);
const longTextSchema = (max) => z.string().trim().max(max);
export const topicPermissionsSchema = z.object({
    canEdit: z.boolean(),
    canChangeStatus: z.boolean(),
    canComment: z.boolean(),
    canProposeOption: z.boolean(),
    canManageRounds: z.boolean(),
    canRecordDecision: z.boolean(),
    canAttach: z.boolean(),
});
export const topicSummarySchema = z.object({
    id: uuidSchema,
    number: z.number().int(),
    title: z.string(),
    status: topicStatusSchema,
    priority: prioritySchema,
    visibility: topicVisibilitySchema,
    category: categorySchema,
    decisionDeadline: isoDateTimeSchema.nullable(),
    noDeadline: z.boolean(),
    author: memberRefSchema,
    owner: memberRefSchema.nullable(),
    commentCount: z.number().int(),
    /** The caller is a REQUIRED participant and the topic is still open for input. */
    awaitingMyInput: z.boolean(),
    /** The caller is eligible in an OPEN round and has not voted yet. */
    myVotePending: z.boolean(),
    following: z.boolean(),
    statusChangedAt: isoDateTimeSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const topicPageSchema = cursorPageSchema(topicSummarySchema);
export const TOPIC_LIST_VIEWS = ['ACTIVE', 'CLOSED', 'ALL'];
export const topicListViewSchema = z.enum(TOPIC_LIST_VIEWS);
export const topicListQuerySchema = z.object({
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    view: topicListViewSchema.default('ACTIVE'),
    status: topicStatusSchema.optional(),
    priority: prioritySchema.optional(),
    categoryId: uuidSchema.optional(),
    ownerMemberId: uuidSchema.optional(),
    participantMemberId: uuidSchema.optional(),
    /** Full-text search over title, context and desired outcome. */
    q: z.string().trim().min(2).max(200).optional(),
});
export const topicParticipantSchema = memberRefSchema.extend({
    kind: participantKindSchema,
    addedAt: isoDateTimeSchema,
});
export const topicStatusHistoryItemSchema = z.object({
    fromStatus: topicStatusSchema.nullable(),
    toStatus: topicStatusSchema,
    reason: z.string().nullable(),
    changedBy: z.string().nullable(),
    changedAt: isoDateTimeSchema,
});
export const ATTACHMENT_KINDS = ['FILE', 'LINK'];
export const attachmentSchema = z.object({
    id: uuidSchema,
    kind: z.enum(ATTACHMENT_KINDS),
    title: z.string().nullable(),
    url: z.string().nullable(),
    attachedBy: z.string(),
    attachedAt: isoDateTimeSchema,
});
export const httpUrlSchema = z
    .string()
    .trim()
    .max(2048)
    .pipe(z.url({ protocol: /^https?$/, message: 'Enter an http(s) link' }));
export const createLinkAttachmentRequestSchema = z.object({
    url: httpUrlSchema,
    title: z.string().trim().min(1).max(200),
});
export const TOPIC_OPTION_STATUSES = ['ACTIVE', 'WITHDRAWN'];
export const topicOptionStatusSchema = z.enum(TOPIC_OPTION_STATUSES);
export const topicOptionSchema = z.object({
    id: uuidSchema,
    title: z.string(),
    description: z.string(),
    pros: z.string().nullable(),
    cons: z.string().nullable(),
    position: z.number().int(),
    status: topicOptionStatusSchema,
    author: memberRefSchema,
    withdrawnAt: isoDateTimeSchema.nullable(),
    /** Appears on the ballot of a non-draft round, so its wording is frozen as evidence. */
    locked: z.boolean(),
    createdAt: isoDateTimeSchema,
    version: versionSchema,
});
export const topicDetailSchema = topicSummarySchema.extend({
    context: z.string(),
    desiredOutcome: z.string(),
    pinnedSummary: z.string().nullable(),
    meetingRequiredReason: z.string().nullable(),
    closedAt: isoDateTimeSchema.nullable(),
    participants: z.array(topicParticipantSchema),
    statusHistory: z.array(topicStatusHistoryItemSchema),
    options: z.array(topicOptionSchema),
    attachments: z.array(attachmentSchema),
    /** Statuses the caller may move this topic to right now. */
    allowedStatusChanges: z.array(manualTopicStatusSchema),
    permissions: topicPermissionsSchema,
    version: versionSchema,
});
export const participantInputSchema = z.object({
    memberId: uuidSchema,
    kind: participantKindSchema,
});
const uniqueParticipants = (items) => new Set(items.map((item) => item.memberId)).size === items.length;
function deadlineChoiceIsConsistent(value) {
    return !(value.noDeadline === true && typeof value.decisionDeadline === 'string');
}
export const createTopicRequestSchema = z
    .object({
    title: topicTitleSchema,
    context: longTextSchema(20000).default(''),
    desiredOutcome: longTextSchema(5000).default(''),
    categoryId: uuidSchema,
    priority: prioritySchema.default('NORMAL'),
    decisionDeadline: isoDateTimeSchema.nullable().default(null),
    noDeadline: z.boolean().default(false),
    visibility: topicVisibilitySchema.default('WORKSPACE'),
    ownerMemberId: uuidSchema.nullable().default(null),
    participants: z
        .array(participantInputSchema)
        .max(50)
        .default([])
        .refine(uniqueParticipants, { message: 'Each member can be listed once' }),
    /** `false` keeps the topic as a private DRAFT of its author. */
    publish: z.boolean().default(true),
})
    .refine(deadlineChoiceIsConsistent, {
    message: 'Choose either a decision deadline or "no deadline"',
    path: ['noDeadline'],
})
    .refine((value) => !value.publish || value.noDeadline || value.decisionDeadline !== null, {
    message: 'A published topic needs a decision deadline or "no deadline"',
    path: ['decisionDeadline'],
})
    .refine((value) => !value.publish || value.context.length > 0, {
    message: 'Describe the context before publishing',
    path: ['context'],
})
    .refine((value) => !value.publish || value.desiredOutcome.length > 0, {
    message: 'Describe the desired outcome before publishing',
    path: ['desiredOutcome'],
});
export const updateTopicRequestSchema = z
    .object({
    version: versionSchema,
    title: topicTitleSchema.optional(),
    context: longTextSchema(20000).optional(),
    desiredOutcome: longTextSchema(5000).optional(),
    categoryId: uuidSchema.optional(),
    priority: prioritySchema.optional(),
    decisionDeadline: isoDateTimeSchema.nullable().optional(),
    noDeadline: z.boolean().optional(),
    ownerMemberId: uuidSchema.nullable().optional(),
    pinnedSummary: longTextSchema(5000).nullable().optional(),
})
    .refine(deadlineChoiceIsConsistent, {
    message: 'Choose either a decision deadline or "no deadline"',
    path: ['noDeadline'],
});
export const replaceParticipantsRequestSchema = z.object({
    version: versionSchema,
    visibility: topicVisibilitySchema,
    participants: z
        .array(participantInputSchema)
        .max(50)
        .refine(uniqueParticipants, { message: 'Each member can be listed once' }),
});
/** Statuses that need a recorded reason (exits and re-openings). */
export const TOPIC_STATUSES_REQUIRING_REASON = [
    'DEFERRED',
    'CANCELLED',
    'MEETING_REQUIRED',
    'NO_ACTION',
];
export const changeTopicStatusRequestSchema = z
    .object({
    version: versionSchema,
    status: manualTopicStatusSchema,
    reason: reasonSchema.optional(),
})
    .refine((value) => value.reason !== undefined ||
    !TOPIC_STATUSES_REQUIRING_REASON.includes(value.status), { message: 'A reason is required for this status', path: ['reason'] });
export const similarTopicsQuerySchema = z.object({
    title: z.string().trim().min(3).max(200),
    excludeTopicId: uuidSchema.optional(),
});
export const similarTopicSchema = z.object({
    id: uuidSchema,
    number: z.number().int(),
    title: z.string(),
    status: topicStatusSchema,
    /** pg_trgm similarity in [0, 1], formatted with two decimals. */
    similarity: z.string(),
});
export const similarTopicListSchema = z.object({ items: z.array(similarTopicSchema) });
export const followStateSchema = z.object({ following: z.boolean() });
// --- Discussion ----------------------------------------------------------------------------------
/** Reaction codes; the UI maps them to symbols with accessible labels. */
export const REACTIONS = [
    'THUMBS_UP',
    'THUMBS_DOWN',
    'HEART',
    'CELEBRATE',
    'THINKING',
    'EYES',
];
export const reactionSchema = z.enum(REACTIONS);
export const commentSchema = z.object({
    id: uuidSchema,
    parentCommentId: uuidSchema.nullable(),
    author: memberRefSchema,
    /** `null` once the comment is deleted (tombstone); revisions remain as evidence. */
    body: z.string().nullable(),
    isKeyArgument: z.boolean(),
    mentions: z.array(memberRefSchema),
    reactions: z.array(z.object({ reaction: reactionSchema, count: z.number().int(), mine: z.boolean() })),
    revisionCount: z.number().int(),
    editedAt: isoDateTimeSchema.nullable(),
    deletedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    canEdit: z.boolean(),
    version: versionSchema,
});
export const commentPageSchema = cursorPageSchema(commentSchema);
export const commentBodySchema = z.string().trim().min(1).max(20000);
export const createCommentRequestSchema = z.object({
    body: commentBodySchema,
    parentCommentId: uuidSchema.nullable().optional(),
    mentionMemberIds: z.array(uuidSchema).max(20).default([]),
});
export const editCommentRequestSchema = z.object({
    version: versionSchema,
    body: commentBodySchema,
    mentionMemberIds: z.array(uuidSchema).max(20).default([]),
});
export const versionedRequestSchema = z.object({ version: versionSchema });
export const markKeyArgumentRequestSchema = z.object({
    version: versionSchema,
    isKeyArgument: z.boolean(),
});
export const toggleReactionRequestSchema = z.object({ reaction: reactionSchema });
export const commentRevisionSchema = z.object({
    revision: z.number().int(),
    body: z.string(),
    editedBy: z.string(),
    createdAt: isoDateTimeSchema,
});
export const commentRevisionListSchema = z.object({ items: z.array(commentRevisionSchema) });
// --- Options / proposals -------------------------------------------------------------------------
const optionFieldsSchema = z.object({
    title: topicTitleSchema,
    description: z.string().trim().min(1).max(10000),
    pros: longTextSchema(5000).nullable().optional(),
    cons: longTextSchema(5000).nullable().optional(),
});
export const createTopicOptionRequestSchema = optionFieldsSchema;
export const updateTopicOptionRequestSchema = optionFieldsSchema
    .partial()
    .extend({ version: versionSchema });
export const withdrawTopicOptionRequestSchema = z.object({
    version: versionSchema,
    reason: reasonSchema,
});
//# sourceMappingURL=topics.js.map